"use client";

import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";
import { authLink } from "@/lib/apollo/links/authLink";
import { errorLink } from "@/lib/apollo/links/errorLink";
import { requireAuthLink } from "@/lib/apollo/links/requireAuthLink";
import pkg from "../../package.json";

export function makeApolloClient() {
  const supergraphUrl = process.env.NEXT_PUBLIC_SUPERGRAPH_URL;
  if (!supergraphUrl) {
    throw new Error(
      "Missing required environment variable: NEXT_PUBLIC_SUPERGRAPH_URL. " +
        "Please configure the GraphQL supergraph URL in your .env.local file."
    );
  }

  const httpLink = new HttpLink({
    uri: supergraphUrl,
  });

  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // Fix for cache warnings: merge different me queries properly
          me: {
            merge(existing, incoming) {
              // Deeply merge the me object to preserve all fields from different queries
              return {
                ...existing,
                ...incoming,
              };
            },
          },
          devices: {
            keyArgs: ["input", ["filter", "workspaceId"]],
            merge(existing, incoming, { args }) {
              const incomingPage = args?.input?.pagination?.offset?.page || 1;

              // Always use incoming data for page 1 (fresh data)
              if (!existing || incomingPage === 1) {
                return incoming;
              }

              // For subsequent pages, append to existing data
              if (existing?.data && incoming?.data) {
                return {
                  ...incoming, // Use incoming meta/error
                  data: [...existing.data, ...incoming.data],
                };
              }

              return incoming;
            },
          },
          sites: {
            // Include perPage and search in cache key to avoid conflicts
            // Different searches and page sizes should have separate cache entries
            keyArgs: ["input", ["filter", "workspaceId", "pagination", ["offset", ["perPage"]], "search"]],
            merge(existing, incoming, { args }) {
              const incomingPage = args?.input?.pagination?.offset?.page || 1;

              // Always use incoming data for page 1 (fresh data)
              if (!existing || incomingPage === 1) {
                return incoming;
              }

              // For subsequent pages, append to existing data
              if (existing?.data && incoming?.data) {
                return {
                  ...incoming, // Use incoming meta/error
                  data: [...existing.data, ...incoming.data],
                };
              }

              return incoming;
            },
          },
        },
      },
    },
  });

  return new ApolloClient({
    link: ApolloLink.from([errorLink, requireAuthLink, authLink, httpLink]),
    cache,
    clientAwareness: {
      name: pkg.name,
      version: process.env.NEXT_PUBLIC_GIT_SHA ?? "dev",
    },
    devtools: { enabled: true, name: "Admin" },
    defaultOptions: {
      watchQuery: {
        // Use cache-first with persistence for instant UI on subsequent visits
        // The apollo3-cache-persist will ensure cached data is available immediately
        fetchPolicy: "cache-first",
        errorPolicy: "all",
      },
      query: {
        // Use cache-first - with cache persistence, this will show instant UI
        fetchPolicy: "cache-first",
        errorPolicy: "all",
      },
      mutate: {
        errorPolicy: "all",
      },
    },
  });
}
